import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Button } from './Button'
import { CloseIcon } from './icons'

export interface ReviewAcceptDocument {
  title: string
  version: string
  /** Plain-language bullets (newline-separated), shown above the full text. */
  summary: string | null
  rendered_text: string | null
}

export interface ReviewAcceptModalProps {
  open: boolean
  document: ReviewAcceptDocument | null
  /** 'accept' = gated CTA; 'view' = read-only accepted-version viewer. */
  mode: 'accept' | 'view'
  acceptedAt?: string | null
  accepting?: boolean
  error?: string | null
  onAccept?: () => void
  onClose: () => void
}

/**
 * Review & Accept modal — the heart of the acceptance gate.
 *
 * Contract: plain-language summary bullets on top, the FULL legal text in a
 * scrollable pane, and a single primary CTA ("Accept & Claim") that unlocks
 * only after the text has been scrolled to the bottom. Checkbox-free: the
 * click IS the assent. Acceptance is recorded against the exact version shown
 * (user × document_version × server timestamp × ip × user agent).
 */
export function ReviewAcceptModal({
  open,
  document: doc,
  mode,
  acceptedAt,
  accepting = false,
  error = null,
  onAccept,
  onClose,
}: ReviewAcceptModalProps) {
  const titleId = useId()
  const paneRef = useRef<HTMLDivElement | null>(null)
  const [bottomReached, setBottomReached] = useState(false)

  // Reset the scroll gate whenever a new document opens.
  useEffect(() => {
    setBottomReached(false)
    if (!open || !doc) return

    // Short texts: the pane is not scrollable → the whole text is visible →
    // the gate starts unlocked.
    const pane = paneRef.current
    if (pane && pane.scrollHeight <= pane.clientHeight + 8) setBottomReached(true)

    pane?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open, doc])

  const handleScroll = useCallback(() => {
    const pane = paneRef.current
    if (!pane) return
    const atBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 8
    if (atBottom) setBottomReached(true)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !doc) return null

  const bullets = (doc.summary ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  const canAccept = mode === 'accept' && bottomReached && !accepting

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/50" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fade-in relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-ground-2 shadow-lg"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="font-display text-display-m text-ink">
              {doc.title}
            </h2>
            <p className="text-small text-ink-muted">
              Version {doc.version}
              {mode === 'view' && acceptedAt
                ? ` · accepted ${new Date(acceptedAt).toLocaleString()}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-muted hover:bg-ground-3 hover:text-ink"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        {bullets.length > 0 && (
          <div className="border-b border-line bg-ground-3/60 px-6 py-4">
            <p className="text-small font-medium text-ink">In plain language</p>
            <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-small text-ink-muted">
              {bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        <div
          ref={paneRef}
          tabIndex={0}
          onScroll={handleScroll}
          aria-label={`Full text of ${doc.title} version ${doc.version}`}
          className="flex-1 overflow-y-auto px-6 py-5 text-small leading-relaxed text-ink focus-visible:outline-2"
        >
          <p className="whitespace-pre-wrap">{doc.rendered_text ?? 'No text is available for this version.'}</p>
        </div>

        {error && (
          <p role="alert" className="border-t border-line px-6 py-3 text-small text-terracotta-deep">
            {error}
          </p>
        )}

        <footer className="flex flex-col gap-3 border-t border-line px-6 py-5">
          {mode === 'accept' ? (
            <>
              <p aria-live="polite" className="text-small text-ink-muted">
                {bottomReached
                  ? 'By clicking Accept & Claim you agree to this exact version.'
                  : 'Scroll to the end of the document to continue.'}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <Button variant="ghost" onClick={onClose} disabled={accepting}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={onAccept} disabled={!canAccept} loading={accepting}>
                  Accept &amp; Claim
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </footer>
      </div>
    </div>
  )
}
